import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getLecturers, createLecturer, deleteLecturer, getCourses, initiateCall } from '../services/api';
import { exportTableToPDF } from '../utils/pdfExport';

export default function Lecturers() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [lecturers, setLecturers] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', firstNameAr: '', lastNameAr: '',
    email: '', phone: '', specialization: '', contractType: 'full', hourlyRate: 0,
    performedBy: ''
  });

  const loadData = () => {
    getLecturers().then(res => setLecturers(res.data)).catch(() => {});
    getCourses().then(res => setCourses(res.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  const handleAdd = async () => {
    await createLecturer(form);
    setShowModal(false);
    setForm({ firstName: '', lastName: '', firstNameAr: '', lastNameAr: '', email: '', phone: '', specialization: '', contractType: 'full', hourlyRate: 0, performedBy: userName });
    loadData();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('האם למחוק?')) {
      await deleteLecturer(id);
      loadData();
    }
  };

  const getName = (l: any) => {
    if (lang === 'ar' && l.firstNameAr) return `${l.firstNameAr} ${l.lastNameAr}`;
    return `${l.firstName} ${l.lastName}`;
  };

  const getLecturerCourses = (lecId: string) => {
    return courses.filter(c => c.lecturerId === lecId);
  };

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">👨‍🏫 {t('lecturerList')}</span>
        <button className="btn btn-primary" onClick={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}>+ {t('addLecturer')}</button>
      </div>

      <div className="page-section">
        <div className="toolbar">
          <button
            className="btn"
            style={{ backgroundColor: '#B71C1C', color: '#fff', border: 'none' }}
            onClick={() => {
              const headers = ['#', t('name'), t('specialization'), t('email'), t('phone'), t('contractType'), t('hourlyRate'), t('courses')];
              const rows = lecturers.map((l, i) => [
                String(i + 1), getName(l), l.specialization, l.email, l.phone,
                l.contractType === 'full' ? t('fullTime') : t('partTime'),
                `${l.hourlyRate} ₪`,
                getLecturerCourses(l.id).map(c => c.name).join(', ') || '-'
              ]);
              const date = new Date().toISOString().split('T')[0];
              exportTableToPDF(t('lecturerList'), headers, rows, `lecturers_${date}.pdf`);
            }}
          >{t('exportPDF')} 📄</button>
        </div>
        <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('name')}</th>
              <th>{t('specialization')}</th>
              <th>{t('email')}</th>
              <th>{t('phone')}</th>
              <th>{t('contractType')}</th>
              <th>{t('hourlyRate')}</th>
              <th>{t('courses')}</th>
              <th>{t('performedBy')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {lecturers.map((l, i) => (
              <tr key={l.id}>
                <td>{i + 1}</td>
                <td><strong style={{ color: '#7B1FA2' }}>{getName(l)}</strong></td>
                <td>{l.specialization}</td>
                <td dir="ltr">{l.email}</td>
                <td dir="ltr" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {l.phone}
                  {l.phone && <button onClick={() => initiateCall({ phoneNumber: l.phone, entityType: 'lecturer', entityId: l.id, entityName: getName(l), calledBy: userName }).then(() => alert(lang === 'ar' ? 'بدأت المكالمة!' : 'השיחה התחילה!'))} style={{ background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>📞</button>}
                </td>
                <td><span className={`badge ${l.contractType === 'full' ? 'badge-green' : 'badge-yellow'}`}>
                  {l.contractType === 'full' ? t('fullTime') : t('partTime')}
                </span></td>
                <td>{l.hourlyRate} ₪</td>
                <td>{getLecturerCourses(l.id).map(c => c.name).join(', ') || '-'}</td>
                <td>{l.performedBy || '-'}</td>
                <td>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(l.id)}>{t('delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {lecturers.length === 0 && <div className="empty-state"><div className="icon">👨‍🏫</div><p>{t('noData')}</p></div>}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('addLecturer')}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (עברית)</label>
                <input className="form-input" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (עברית)</label>
                <input className="form-input" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (ערבית)</label>
                <input className="form-input" value={form.firstNameAr} onChange={e => setForm({...form, firstNameAr: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (ערבית)</label>
                <input className="form-input" value={form.lastNameAr} onChange={e => setForm({...form, lastNameAr: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input className="form-input" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('specialization')}</label>
                <input className="form-input" value={form.specialization} onChange={e => setForm({...form, specialization: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('contractType')}</label>
                <select className="form-select" value={form.contractType} onChange={e => setForm({...form, contractType: e.target.value})}>
                  <option value="full">{t('fullTime')}</option>
                  <option value="partial">{t('partTime')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('hourlyRate')} (₪)</label>
                <input className="form-input" type="number" value={form.hourlyRate} onChange={e => setForm({...form, hourlyRate: parseInt(e.target.value)})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('actionPerformer')}</label>
              <input className="form-input" value={form.performedBy} onChange={e => setForm({...form, performedBy: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleAdd}>{t('save')}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
